import type { AxiosError } from 'axios';
import { getYear, isSameDay, isSameMonth, isWeekend, parse } from 'date-fns';
import { sum } from 'lodash';
import { createQuery } from 'react-query-kit';

import { Env } from '@/lib';
import { getMemoDays, memoHolidays } from '@/utils/work-time-utils';

import { client, queryClient } from '../../common';
import { useHolidays } from '../../holidays';
import {
  entriesSchema,
  Entry,
  MonthEntries,
  monthEntriesSchema,
} from './types';

type Response = MonthEntries;
type Variables = { focusDate: string; mockUser?: number };

export const useMonthEntries = createQuery<Response, Variables, AxiosError>({
  queryKey: ['entries'],
  refetchOnMount: false,
  fetcher: async ({ focusDate, mockUser }, { signal }) => {
    const response = await client({
      method: 'GET',
      url: Env.API_URL + '/api/entries',
      params: { focusDate, mockUser },
      signal,
    });

    const parsedFocusDate = parse(focusDate, 'yyyy-MM-dd', new Date());

    const holidays = memoHolidays(
      getYear(parsedFocusDate),
      await queryClient.ensureQueryData(useHolidays.getFetchOptions()),
    );

    const entries = entriesSchema.parse(response.data);

    const monthEntries = getMemoDays(focusDate)
      .flatMap((week) => week)
      .map((d) => {
        if (isSameMonth(d, parsedFocusDate)) {
          const isHoliday = Boolean(holidays.find((h) => isSameDay(h, d)));
          const _isWeekend = isWeekend(d);
          return {
            date: d,
            entries: entries.filter((e) => isSameDay(e.fromTime, d)),
            minutesWorked: getMinutesWorkedInDay(d, entries),
            isHoliday: isHoliday,
            isWeekend: _isWeekend,
            isCurrentMonth: true,
            isBusinessDay: !isHoliday && !_isWeekend,
          };
        }

        return {
          date: d,
          entries: [], // i giorni fuori dal mese non ci interessano
          minutesWorked: 0, // i giorni fuori dal mese non ci interessano
          isHoliday: false, // i giorni fuori dal mese non ci interessano
          isWeekend: false, // i giorni fuori dal mese non ci interessano
          isBusinessDay: false, // i giorni fuori dal mese non ci interessano
          isCurrentMonth: false,
        };
      });

    const parsedResult = await monthEntriesSchema.parseAsync(monthEntries);
    //console.log('%o', parsedResult);
    return parsedResult;
  },
});

const getMinutesWorkedInDay = (date: Date, entries: Entry[] = []) => {
  return (
    sum(
      entries
        .filter((e) => isSameDay(e.fromTime, date))
        .map((e) => e.effectiveHours),
    ) * 60
  );
};

// const getMinutesWorkedInDay_OLD = (date: Date, entries: Entry[] = []) => {
//   if (entries.length === 0) {
//     return 0;
//   }

//   const sameDayDates = entries.filter((e) => isSameDay(e.fromTime, date));
//   if (sameDayDates.length === 0) {
//     return 0;
//   }

//   let timePoints: { date: Date; type: 'start' | 'end' }[] = [];
//   for (const day of sameDayDates) {
//     timePoints.push({ date: day.fromTime, type: 'start' });
//     timePoints.push({ date: day.toTime, type: 'end' });
//   }

//   timePoints = sortBy(timePoints, (t) => t.date);

//   let minutes = 0;
//   let currentStartDate: Date | null = null;
//   let runningTimelines = 0;

//   for (const t of timePoints) {
//     if (runningTimelines == 0) {
//       currentStartDate = t.date;
//     }

//     if (runningTimelines === 1 && t.type === 'end' && currentStartDate) {
//       minutes = minutes + differenceInMinutes(t.date, currentStartDate);
//       currentStartDate = null;
//     }

//     if (t.type === 'start') {
//       runningTimelines++;
//     }

//     if (t.type === 'end') {
//       runningTimelines--;
//     }
//   }

//   return minutes;
// };
